import { useApi } from '@gravitate-js/the-armory'
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { getAPIURL } from '@utils/api'
import dayjs from 'dayjs'
import {
  type BookType,
  TransactionLeg,
  type TransactionDetail,
  type TransactionRow,
  type TransactionResponse,
  type BaseTransactionQueryParams,
  type DatedTransactionQueryParams,
  type TransactionView,
  type FreightTransactionDetailOverrideReq,
  type FreightTransactionAddManualAccessorialReq,
  type BaseTransactionDetailReq,
  type FreightTransactionRemoveManualAccessorialReq,
  type FreightOverviewRequest,
  TransactionFilterParam,
  TransactionStatus,
  type FreightStatus,
  FreightType,
  TransactionQueryKeys,
} from './types'
import { useApiClient } from '@/hooks/useApiClient'

// Helper function to get default date range (used for Posted query default)
const getDefaultPostedDateRange = () => {
  const end = dayjs() // Today
  const start = dayjs().subtract(7, 'day') // 7 days ago

  return {
    from_date: start.toDate(),
    to_date: end.toDate(),
  }
}
const addContentToDetails = (rows: TransactionRow[]) => {
  return rows.map((row) => ({
    ...row,
    details: row.details.map((detail) => ({
      ...detail,
      book_type: row.book_type,
      order_id: row.order_id,
      order_number: row.order_number,
      customer_id: row.customer_id,
    })),
  }))
}

/**
 * Helper function to flatten transaction details from rows into a single array
 *
 * This function takes the nested structure of transaction rows with details
 * and flattens it into a single array of transaction details with additional
 * context from their parent rows. This makes the data easier to display in flat
 * grid views and simplifies filtering and sorting.
 *
 * The function enriches each detail with:
 * 1. Consistent book_type (Cost/Revenue)
 * 2. Parent row identifiers (order_id, order_number)
 * 3. Counterparty from the most appropriate source
 * 4. Product grouping information
 * 5. Status from the parent row
 *
 * @param rows - Array of transaction rows, each containing nested details
 * @param bookType - Whether this is a "Cost" or "Revenue" transaction
 * @returns Flattened array of transaction details with context from parent rows
 */
const flattenTransactionDetails = (rows: TransactionRow[] | undefined, bookType: BookType): TransactionDetail[] => {
  if (!rows) return []
  return rows.flatMap((row) =>
    row.details.map((detail) => ({
      ...detail,
      book_type: bookType, // Ensure book_type consistency if needed later
      order_id: row.order_id,
      order_number: row.order_number,
      po: row.po,
      // Determine the most appropriate counterparty name from available sources
      // This ensures we always have the best counterparty info for display
      counterparty: row.customer_name || row.carrier_name || detail.counterparty,
      product_group: detail.product_group, // Detail level is usually more specific
      customer_id: row.customer_id, // Add customer_id from parent row
      is_multi_customer: row.is_multi_customer,
      new_version_available: row.new_version_available,
    }))
  )
}

// --- Main Hook ---
export const useTransactions = () => {
  const api = useApi({ baseURLOverride: getAPIURL() })
  const apiClient = useApiClient()
  const queryClient = useQueryClient()

  // --- Individual Query Hooks ---

  /**
   * Utility function to process transaction API responses into a consistent format
   *
   * This function standardizes how we handle transaction responses across different queries,
   * eliminating code duplication and ensuring consistent transformation.
   *
   * The function:
   * 1. Safely extracts rows from the response (with fallback to empty array)
   * 2. Creates a TransactionView with both the original rows and flattened details
   * 3. Passes through the original params for context
   *
   * @param response - The raw API response with transaction data
   * @param costRevenue - Whether this is "Cost" or "Revenue" data
   * @returns A standardized TransactionView object for consistent UI rendering
   */
  const processTransactionResponse = (response: TransactionResponse, costRevenue: BookType): TransactionView => {
    const rows = response?.rows || []
    return {
      rows: addContentToDetails(rows), // Return raw rows for by-order view
      details: flattenTransactionDetails(rows, costRevenue), // Flatten for flat view
      params: response?.params,
    }
  }

  const useEstimatedTransactionsQuery = (params: BaseTransactionQueryParams): UseQueryResult<TransactionView> => {
    const { filter, costRevenue, from_date, to_date } = params

    return useQuery({
      queryKey: [TransactionQueryKeys.estimated, costRevenue, filter, from_date, to_date],
      queryFn: async (): Promise<TransactionView> => {
        const requestBody: FreightOverviewRequest = {
          filter,
          from_date: from_date?.toISOString(),
          to_date: to_date?.toISOString(),
          freight_type: costRevenue === 'Cost' ? 'estimated_cost' : 'estimated_revenue',
        }
        const response: TransactionResponse = await api.post('freight/transaction/overview', requestBody)

        // Process transaction data using common function
        return processTransactionResponse(response, costRevenue)
      },
      enabled: !!costRevenue && !!from_date && !!to_date,
      refetchOnWindowFocus: false,
    })
  }

  const useOpenTransactionsQuery = (params: BaseTransactionQueryParams): UseQueryResult<TransactionView> => {
    const { filter, costRevenue, from_date, to_date } = params
    const enabled = !!costRevenue
    return useQuery({
      queryKey: [TransactionQueryKeys.open, costRevenue, filter, from_date, to_date],
      queryFn: async (): Promise<TransactionView> => {
        const requestBody: FreightOverviewRequest = {
          filter,
          from_date: from_date?.toISOString(),
          to_date: to_date?.toISOString(),
          freight_type: costRevenue === 'Cost' ? 'cost' : 'revenue',
          freight_status: 'open', // Keep status as open
        }
        const response: TransactionResponse = await api.post('freight/transaction/overview', requestBody)

        // Process transaction data using common function
        return processTransactionResponse(response, costRevenue)
      },
      enabled,
      refetchOnWindowFocus: false,
    })
  }

  const usePostedTransactionsQuery = (params: DatedTransactionQueryParams): UseQueryResult<TransactionView> => {
    const { filter, costRevenue, from_date, to_date } = params
    return useQuery({
      queryKey: [TransactionQueryKeys.posted, costRevenue, filter, from_date, to_date],
      queryFn: async (): Promise<TransactionView> => {
        const requestBody: FreightOverviewRequest = {
          filter,
          from_date: from_date?.toISOString(),
          to_date: to_date?.toISOString(),
          freight_type: costRevenue === 'Cost' ? 'cost' : 'revenue',
          freight_status: 'posted',
        }
        const response: TransactionResponse = await api.post('freight/transaction/overview', requestBody)

        // Process transaction data using common function
        return processTransactionResponse(response, costRevenue)
      },
      enabled: !!costRevenue && !!from_date && !!to_date, // Ensure dates are provided
      refetchOnWindowFocus: false,
    })
  }

  // --- Keep existing mutations and other specific queries ---

  /**
   * Query hook to fetch transactions for a specific order
   *
   * @param orderNumber - The order number to fetch transactions for
   * @param costRevenue - Whether to fetch "Cost" or "Revenue" transactions
   * @param status - Optional filter for transaction status
   * @returns The flattened transaction details
   *
   * Note: The filter in this function creates a valid set of transactions to work with,
   * filtering rows that match specific criteria that the BE may not handle.
   * This ensures we have the correct subset of data to display.
   */
  const useTransactionByOrderQuery = ({
    orderNumber,
    costRevenue,
    status,
  }: {
    orderNumber?: string
    costRevenue: BookType
    // Add optional status parameter if needed for specific context
    status?: FreightStatus | 'estimated' | null
  }): UseQueryResult<TransactionDetail[]> =>
    // Return flat details directly
    useQuery({
      queryKey: [TransactionQueryKeys.byOrder, orderNumber, costRevenue, status], // Add costRevenue & status to key
      queryFn: async () => {
        let freight_type: FreightOverviewRequest['freight_type']
        let freight_status: FreightOverviewRequest['freight_status']

        if (status === 'estimated') {
          freight_type = costRevenue === 'Cost' ? 'estimated_cost' : 'estimated_revenue'
          freight_status = null // API default for estimated
        } else {
          freight_type = costRevenue === 'Cost' ? 'cost' : 'revenue'
          freight_status = status // 'open', 'posted', or null (for all actual)
        }

        const requestBody: FreightOverviewRequest = {
          // Use wide date range only if status is not specified (fetch all)?
          // Otherwise, maybe rely on default range or context-specific dates.
          // Keeping wide range for now for compatibility.
          from_date: dayjs().subtract(3, 'year').toISOString(),
          to_date: dayjs().toISOString(),
          filter: { order: orderNumber as string },
          freight_type: freight_type,
          freight_status: freight_status,
        }
        const response: TransactionResponse = await api.post('freight/transaction/overview', requestBody)

        // Client-side filtering might be needed here too if status param is used and backend filter fails
        let rows = response?.rows || []
        if (status === 'open') {
          rows = rows.filter((row) => row.status !== 'posted')
        } else if (status === 'posted') {
          rows = rows.filter((row) => row.status === 'posted')
        } // 'estimated' and null don't need filtering based on current logic

        // For this hook, we only need the flattened details
        return flattenTransactionDetails(rows, costRevenue)
      },
      refetchOnWindowFocus: false,
      // Only enable if orderNumber and costRevenue are provided
      enabled: !!orderNumber && !!costRevenue,
    })

  // --- Mutations (remain largely unchanged) ---

  const useOverrideTransactionMutation = useMutation({
    mutationFn: async (data: FreightTransactionDetailOverrideReq) => {
      await api.post('freight/transaction/override', data)
    },
    onSuccess: () => {
      return Promise.all(
        Object.values(TransactionQueryKeys).map((v) => queryClient.invalidateQueries({ queryKey: [v] }))
      )
    },
    onError: (error) => {
      console.error('Error overriding transaction:', error)
    },
  })

  const useAddManualAccessorialMutation = useMutation({
    mutationFn: async (data: FreightTransactionAddManualAccessorialReq) => {
      await api.post('freight/transaction/add_manual_accessorial', data)
    },
    onSuccess: () => {
      return Promise.all(
        Object.values(TransactionQueryKeys).map((v) => queryClient.invalidateQueries({ queryKey: [v] }))
      )
    },
    onError: (error) => {
      console.error('Error adding manual accessorial:', error)
    },
  })

  const useAddBaseFreightMutation = apiClient.useMutation('post', '/freight/transaction/add_base_freight', {
    onSuccess: () => {
      return Promise.all(
        Object.values(TransactionQueryKeys).map((v) => queryClient.invalidateQueries({ queryKey: [v] }))
      )
    },
    onError: (error) => {
      console.error('Error adding base freight:', error)
    },
  })

  const useResetTransactionMutation = apiClient.useMutation('post', '/freight/transaction/reset', {
    onSuccess: () => {
      return Promise.all(
        Object.values(TransactionQueryKeys).map((v) => queryClient.invalidateQueries({ queryKey: [v] }))
      )
    },
    onError: (error) => {
      console.error('Unable to reset transaction:', error)
    },
  })

  const useApproveTransactionMutation = useMutation({
    mutationFn: async (data: BaseTransactionDetailReq) => {
      const response = await api.post('freight/transaction/approve', data)
      return response
    },
    onError: (error) => {
      console.error('Error approving transaction:', error)
    },
  })
  const useRejectTransactionMutation = useMutation({
    mutationFn: async (data: BaseTransactionDetailReq) => {
      const response = await api.post('freight/transaction/reject', data)
      return response
    },
    onError: (error) => {
      console.error('Error rejecting transaction:', error) // Corrected log message
    },
  })

  const useRemoveManualAccessorialMutation = useMutation({
    mutationFn: async (data: FreightTransactionRemoveManualAccessorialReq) => {
      await api.post('freight/transaction/remove_manual_accessorial', data)
    },
    onError: (error) => {
      console.error('Error removing manual accessorial:', error)
    },
  })

  const useRefreshTransactionMutation = useMutation({
    mutationFn: async ({
      order_id,
      book_type,
    }: {
      order_id: string
      book_type: BookType
    }) => {
      // Refresh might need status context now? Assuming it refreshes based on order_id primarily.
      // The API endpoint might implicitly handle status or need adjustment.
      await api.post(
        'freight/transaction/refresh',
        {},
        {
          queryParams: new URLSearchParams({ order_id, book_type }),
        }
      )
    },
    onSuccess: () => {
      return Promise.all(
        Object.values(TransactionQueryKeys).map((v) => queryClient.invalidateQueries({ queryKey: [v] }))
      )
    },
    onError: (error) => {
      console.error('Error refreshing transaction:', error)
    },
  })

  // --- Return new structure ---
  return {
    // New Queries
    useEstimatedTransactionsQuery,
    useOpenTransactionsQuery,
    usePostedTransactionsQuery,
    // Existing specific query (adjusted)
    useTransactionByOrderQuery,
    // Mutations
    useRejectTransactionMutation,
    useOverrideTransactionMutation,
    useAddManualAccessorialMutation,
    useApproveTransactionMutation,
    useRemoveManualAccessorialMutation,
    useRefreshTransactionMutation,
    useAddBaseFreightMutation,
    useResetTransactionMutation,
    // Helpers
    getDefaultPostedDateRange, // Export new default range function
  }
}

// // Type Export // Removed to fix linter conflict
// export type { TransactionView, TransactionDetail, TransactionRow };
